import { useState } from 'react'
import { Plus, VideoOff } from 'lucide-react'

import { CameraCard } from './camera-card'
import { CameraFormDialog } from './camera-form-dialog'
import { CameraLiveDialog } from './camera-live-dialog'
import { CameraLocationDialog } from './camera-location-dialog'
import type { Camera } from './camera-types'
import type { CameraTestResult } from '@/lib/api/cameras-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { growerLabel } from '@/components/growers/grower-search-select'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useIrrigationDevices } from '@/hooks/use-irrigation-devices-query'
import {
    useCameras,
    useDeleteCamera,
    useSaveCamera,
    useSetCameraLocation,
    useTestCamera,
} from '@/hooks/use-cameras-query'
import { m } from '@/lib/paraglide/messages'

/**
 * Mọi nơi camera đang đặt: "PHẠM TIẾN ĐẠT (BÁC ĐẠT) · Bãi Anh Hà · Trạm Bơm
 * đồi 1", hoặc "Chưa gắn vào đâu". `stationName` đổi mã trạm ra tên (không
 * có tên thì hiện mã).
 */
export function cameraLocationLabel(
    camera: Camera,
    stationName: (deviceId: string) => string = (id) => id,
): string {
    const parts: string[] = []
    const area = camera.grower_area
    if (area) {
        parts.push(
            m.camera_overview_location({
                grower: growerLabel({
                    name: area.grower_name,
                    nickname: area.grower_nickname ?? undefined,
                }),
                area: area.area_name,
            }),
        )
    }
    for (const d of camera.irrigation_devices) {
        parts.push(m.camera_station_label({ name: stationName(d.device_id) }))
    }
    return parts.length ? parts.join(' · ') : m.camera_location_none()
}

/**
 * Màn quản lý camera — camera là module riêng, không còn nằm trong hộ trồng.
 *
 * Liệt kê MỌI camera (kể cả camera chưa gắn vùng nào), thêm/sửa/xoá, và gắn
 * hoặc chuyển camera sang vùng trồng. Có thể có hàng chục camera nên thẻ chỉ
 * lấy ẢNH TĨNH tự làm mới; luồng liên tục chỉ mở trong hộp thoại chi tiết.
 */
export function CamerasScreen() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('cameras.write')
    const canControl = hasPermission('cameras.control')
    const { data: cameras = [], isLoading } = useCameras()
    // Tên trạm bơm lấy từ danh sách bộ tưới (chỉ đọc); không có quyền xem
    // bộ tưới thì hiện mã trạm.
    const { data: devices = [] } = useIrrigationDevices({
        enabled: hasPermission('irrigation.read'),
        live: false,
    })
    const stationName = (id: string) =>
        devices.find((d) => d.id === id)?.name ?? id
    const locationOf = (camera: Camera) =>
        cameraLocationLabel(camera, stationName)

    const save = useSaveCamera()
    const remove = useDeleteCamera()
    const place = useSetCameraLocation()
    const test = useTestCamera()

    // Giữ id (không giữ vị trí) và chỉ coi là đang mở khi id còn trong danh
    // sách: danh sách tải lại có thể đổi thứ tự hoặc mất camera.
    const [liveId, setLiveId] = useState<string | null>(null)
    const detailOpen = cameras.some((c) => c.id === liveId)

    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<Camera | null>(null)
    const [testResult, setTestResult] = useState<CameraTestResult | null>(null)
    const [placing, setPlacing] = useState<Camera | null>(null)

    const openForm = (camera: Camera | null) => {
        setEditing(camera)
        setTestResult(null)
        setFormOpen(true)
    }
    const closeForm = () => {
        setFormOpen(false)
        setEditing(null)
        setTestResult(null)
    }

    const handleDelete = (camera: Camera) => {
        if (!window.confirm(m.camera_delete_confirm({ name: camera.name }))) {
            return
        }
        void remove.mutateAsync(camera.id).catch(() => undefined)
    }

    return (
        <div className="space-y-4 p-4 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <div className="min-w-0 space-y-1">
                        <h1 className="text-xl font-semibold">
                            {m.camera_overview_title()}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {m.camera_overview_desc()}
                        </p>
                    </div>
                </div>
                {canWrite && (
                    <Button type="button" onClick={() => openForm(null)}>
                        <Plus className="size-4" />
                        {m.grower_area_camera_add()}
                    </Button>
                )}
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </p>
            ) : cameras.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-muted/40 px-6 py-16 text-center">
                    <VideoOff className="size-6 text-muted-foreground" />
                    <p className="text-sm font-medium">
                        {m.camera_overview_empty()}
                    </p>
                </div>
            ) : (
                <>
                    <p className="text-sm text-muted-foreground">
                        {m.camera_overview_count({ count: cameras.length })}
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                        {cameras.map((camera) => (
                            <CameraCard
                                key={camera.id}
                                camera={camera}
                                // Mở chi tiết thì dừng làm mới ảnh ở lưới,
                                // chỉ còn luồng trong hộp thoại.
                                active={!detailOpen}
                                mode="snapshot"
                                location={locationOf(camera)}
                                canWrite={canWrite}
                                onLive={() => setLiveId(camera.id)}
                                onEdit={openForm}
                                onPlace={setPlacing}
                                onRemove={handleDelete}
                            />
                        ))}
                    </div>
                </>
            )}

            <CameraFormDialog
                open={formOpen}
                camera={editing}
                isSaving={save.isPending}
                isTesting={test.isPending}
                testResult={testResult}
                onSave={async (values) => {
                    await save.mutateAsync({
                        cameraId: editing?.id,
                        data: values,
                    })
                    closeForm()
                }}
                onTest={() => {
                    if (!editing) return
                    void test
                        .mutateAsync(editing.id)
                        .then(setTestResult)
                        .catch(() => setTestResult(null))
                }}
                onClose={closeForm}
            />

            <CameraLocationDialog
                camera={placing}
                isSaving={place.isPending}
                onSave={async (areaId) => {
                    if (!placing) return
                    await place.mutateAsync({
                        cameraId: placing.id,
                        growerAreaId: areaId,
                    })
                    setPlacing(null)
                }}
                onClose={() => setPlacing(null)}
            />

            <CameraLiveDialog
                cameras={cameras}
                cameraId={liveId}
                onCameraChange={setLiveId}
                canControl={canControl}
                describe={(camera) => `${locationOf(camera)} · ${camera.name}`}
            />
        </div>
    )
}
